import { renderQuality } from './renderQuality.js';

export class Player {
    constructor(x, y, skin = null, name = 'Player') {
        this.x = x;
        this.y = y;
        this.size = 22;
        this.speed = 3;
        this.maxHp = 100;
        this.hp = 100;
        this.coins = 0;
        this.level = 1;
        this.xp = 0;
        this.xpNeeded = 50;
        this.damage = 10;
        this.magnetRange = 0;
        this.weapons = [];
        this.autoShootCooldown = 0;
        this.autoShootMaxCooldown = 20;
        this.vx = 0;
        this.vy = 0;
        this.skin = skin;
        this.name = name;
        this.activePowerUps = [];
        this.invincible = false;
        this.baseSpeed = 3;
        this.baseDamage = 10;
        this.baseMagnetRange = 0;
        this.angle = -Math.PI / 2;
        this.hitFlash = 0;
        this.thrusterPhase = 0;
        this.engineTrail = [];
        this.shootRangeBonus = 0;
        this.armor = 0;
        // Pseudo-3D orientation (smoothed)
        this.bank = 0;       // roll  -1..1 (lean into turns)
        this.pitch = 0;      // nose  -1..1 (dip when accelerating)
        this._prevAngle = this.angle;
        this._turnRate = 0;
        this._prevSpeed = 0;
        // Planet faction ship theme (set via applyFactionTheme)
        this.shipStyle = 'dart';
        this.secondaryColor = '#aa66ff';
        this.factionPrimary = null;
    }

    update(worldWidth, worldHeight) {
        this.x += this.vx;
        this.y += this.vy;

        // Face movement / aim direction
        if (Math.hypot(this.vx, this.vy) > 0.15) {
            const target = Math.atan2(this.vy, this.vx);
            let diff = target - this.angle;
            while (diff > Math.PI) diff -= Math.PI * 2;
            while (diff < -Math.PI) diff += Math.PI * 2;
            this.angle += diff * 0.2;
        }

        // Turn rate for bank (short-window angular velocity)
        let dAng = this.angle - this._prevAngle;
        while (dAng > Math.PI) dAng -= Math.PI * 2;
        while (dAng < -Math.PI) dAng += Math.PI * 2;
        this._turnRate = this._turnRate * 0.72 + dAng * 0.28;
        this._prevAngle = this.angle;

        // Target bank: lean opposite to turn (aircraft feel) when moving
        const speed = Math.hypot(this.vx, this.vy);
        const speedFactor = Math.min(1, speed / 2.2);
        const bankTarget = Math.max(-1, Math.min(1, -this._turnRate * 14 * (0.35 + speedFactor)));
        this.bank += (bankTarget - this.bank) * 0.18;
        // Ease back to flat when not turning
        if (Math.abs(bankTarget) < 0.02) this.bank *= 0.9;

        // Pitch: nose up slightly when boosting, settle when slowing
        const accel = speed - this._prevSpeed;
        this._prevSpeed = speed;
        const pitchTarget = Math.max(-0.55, Math.min(0.55, accel * 2.8 + speed * 0.06));
        this.pitch += (pitchTarget - this.pitch) * 0.12;

        this.vx *= 0.9;
        this.vy *= 0.9;

        this.x = Math.max(this.size, Math.min(worldWidth - this.size, this.x));
        this.y = Math.max(this.size, Math.min(worldHeight - this.size, this.y));

        if (this.hitFlash > 0) this.hitFlash--;

        // Engine trail when moving
        this.thrusterPhase += 0.35 + speed * 0.08;
        const trailCap = renderQuality() === 'high' ? 8 : renderQuality() === 'medium' ? 4 : 0;
        if (trailCap === 0) {
            if (this.engineTrail.length) this.engineTrail.length = 0;
        } else if (speed > 0.4) {
            const backX = this.x - Math.cos(this.angle) * (this.size * 0.55);
            const backY = this.y - Math.sin(this.angle) * (this.size * 0.55);
            let slot = this.engineTrail.length >= trailCap ? this.engineTrail.shift() : null;
            if (!slot) slot = { x: 0, y: 0, life: 12 };
            slot.x = backX;
            slot.y = backY;
            slot.life = 12;
            this.engineTrail.push(slot);
        }
        for (let i = this.engineTrail.length - 1; i >= 0; i--) {
            this.engineTrail[i].life--;
            if (this.engineTrail[i].life <= 0) this.engineTrail.splice(i, 1);
        }
    }

    get color() {
        // Skin overrides planet theme; otherwise use faction primary
        return this.skin?.color || this.factionPrimary || '#00d9ff';
    }

    /** Apply planet faction ship look (hull style + colors). */
    applyFactionTheme(faction) {
        if (!faction) return;
        this.factionPrimary = faction.playerPrimary || '#00d9ff';
        this.secondaryColor = faction.playerSecondary || '#aa66ff';
        this.shipStyle = faction.shipStyle || 'dart';
    }

    draw(ctx) {
        ctx.save();
        const color = this.color;
        const t = Date.now();

        // Magnet range indicator
        if (this.magnetRange > 0) {
            ctx.shadowBlur = 18;
            ctx.shadowColor = '#ffaa00';
            ctx.strokeStyle = '#ffaa00';
            ctx.lineWidth = 2;
            ctx.globalAlpha = 0.22 + Math.sin(t / 150) * 0.1;
            ctx.setLineDash([6, 8]);
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.magnetRange, 0, Math.PI * 2);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.globalAlpha = 1;
        }

        const q = renderQuality();
        // Engine trail — one batched fill unless we're on the high path
        if (q === 'high') {
            for (let i = 0; i < this.engineTrail.length; i++) {
                const p = this.engineTrail[i];
                const a = p.life / 12;
                const r = 3 + (1 - a) * 6;
                const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
                g.addColorStop(0, `rgba(255, 200, 80, ${a * 0.9})`);
                g.addColorStop(1, 'transparent');
                ctx.fillStyle = g;
                ctx.beginPath();
                ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
                ctx.fill();
            }
        } else if (this.engineTrail.length) {
            ctx.globalAlpha = 0.45;
            ctx.fillStyle = '#ff8833';
            ctx.beginPath();
            for (let i = 0; i < this.engineTrail.length; i++) {
                const p = this.engineTrail[i];
                ctx.moveTo(p.x + 3, p.y);
                ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
            }
            ctx.fill();
            ctx.globalAlpha = 1;
        }

        // Invincibility shield
        if (this.invincible) {
            const pulse = 0.45 + Math.sin(t / 90) * 0.25;
            ctx.shadowBlur = 28;
            ctx.shadowColor = '#ffd700';
            ctx.strokeStyle = '#ffd700';
            ctx.lineWidth = 3;
            ctx.globalAlpha = pulse;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.size + 12, 0, Math.PI * 2);
            ctx.stroke();
            // Hex shield segments
            ctx.lineWidth = 1.5;
            ctx.globalAlpha = pulse * 0.7;
            for (let i = 0; i < 6; i++) {
                const a0 = (i / 6) * Math.PI * 2 + t / 400;
                const a1 = ((i + 0.55) / 6) * Math.PI * 2 + t / 400;
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size + 16, a0, a1);
                ctx.stroke();
            }
            ctx.globalAlpha = 1;
        }

        if (q === 'high') {
            const glow = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size * 2.4);
            glow.addColorStop(0, color + '66');
            glow.addColorStop(1, 'transparent');
            ctx.fillStyle = glow;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.size * 2.4, 0, Math.PI * 2);
            ctx.fill();
        }

        // Geometry Wars–style neon dart (with bank / pitch)
        const s = this.size * 1.05;
        const bodyColor = this.hitFlash > 0 ? '#ffffff' : color;
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.angle + Math.PI / 2);
        const bank = this.bank || 0;
        const pitch = this.pitch || 0;
        const sx = Math.max(0.48, Math.cos(bank * 0.85));
        const sy = 1 + Math.abs(bank) * 0.1 + pitch * 0.1;
        ctx.transform(1, pitch * 0.1, bank * 0.2, 1, 0, 0);
        ctx.scale(sx, sy);

        this._drawShipByStyle(ctx, s, bodyColor, bank);

        // Thruster flame (tinted to faction)
        const thrust = 0.6 + Math.sin(this.thrusterPhase) * 0.35;
        const moveBoost = Math.min(1, Math.hypot(this.vx, this.vy) / 2);
        const flameLen = s * (0.7 + thrust * 0.65) * (0.4 + moveBoost);
        const flameGrad = q === 'high'
            ? ctx.createLinearGradient(0, s * 0.25, 0, s * 0.25 + flameLen)
            : null;
        if (flameGrad) {
            flameGrad.addColorStop(0, '#ffffff');
            flameGrad.addColorStop(0.25, this.secondaryColor || '#88eeff');
            flameGrad.addColorStop(0.65, color);
            flameGrad.addColorStop(1, 'transparent');
            ctx.shadowBlur = 14;
            ctx.shadowColor = color;
            ctx.fillStyle = flameGrad;
        } else {
            ctx.shadowBlur = 0;
            ctx.fillStyle = color;
        }
        ctx.beginPath();
        ctx.moveTo(-s * 0.28, s * 0.28);
        ctx.lineTo(0, s * 0.28 + flameLen);
        ctx.lineTo(s * 0.28, s * 0.28);
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        // Draw HUD bits in world space without rotation

        // HP arc around player
        const hpRatio = Math.max(0, this.hp / this.maxHp);
        const arcR = this.size + 10;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.strokeStyle = 'rgba(0,0,0,0.45)';
        ctx.beginPath();
        ctx.arc(this.x, this.y, arcR, -Math.PI * 0.75, Math.PI * 0.75);
        ctx.stroke();
        ctx.strokeStyle = hpRatio > 0.5 ? '#00ff99' : hpRatio > 0.25 ? '#ffcc00' : '#ff3355';
        ctx.shadowBlur = 8;
        ctx.shadowColor = ctx.strokeStyle;
        ctx.beginPath();
        ctx.arc(this.x, this.y, arcR, -Math.PI * 0.75, -Math.PI * 0.75 + Math.PI * 1.5 * hpRatio);
        ctx.stroke();

        // Name plate
        ctx.shadowBlur = 0;
        ctx.font = 'bold 13px Rajdhani, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        const label = this.name || 'Player';
        if (!this._labelFontReady || this._labelCache !== label) {
            const fontReady = document.fonts?.check?.('bold 13px Rajdhani') ?? true;
            this._labelCache = label;
            this._labelFontReady = fontReady;
            this._labelWidth = ctx.measureText(label).width;
        }
        const tw = this._labelWidth;
        const padX = 8;
        const padY = 3;
        const bx = this.x - tw / 2 - padX;
        const by = this.y - this.size - 28;
        ctx.fillStyle = 'rgba(6, 10, 28, 0.72)';
        ctx.strokeStyle = color + '99';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(bx, by, tw + padX * 2, 18, 6);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#ffffff';
        ctx.fillText(label, this.x, by + 15);

        ctx.restore();
    }

    /** Pick hull silhouette from planet shipStyle */
    _drawShipByStyle(ctx, s, color, bank = 0) {
        const style = this.shipStyle || 'dart';
        switch (style) {
            case 'wedge': this._drawWedgeShip(ctx, s, color, bank); break;
            case 'diamond': this._drawDiamondShip(ctx, s, color, bank); break;
            case 'blade': this._drawBladeHull(ctx, s, color, bank); break;
            case 'ring': this._drawRingHull(ctx, s, color, bank); break;
            case 'spiker': this._drawSpikerHull(ctx, s, color, bank); break;
            case 'fin': this._drawFinHull(ctx, s, color, bank); break;
            case 'bolt': this._drawBoltHull(ctx, s, color, bank); break;
            case 'prism': this._drawPrismHull(ctx, s, color, bank); break;
            case 'block': this._drawBlockHull(ctx, s, color, bank); break;
            case 'leaf': this._drawLeafHull(ctx, s, color, bank); break;
            case 'mech': this._drawMechHull(ctx, s, color, bank); break;
            case 'dart':
            default: this._drawNeonDart(ctx, s, color, bank); break;
        }
    }

    /** Geometry Wars–style glowing dart / arrow ship */
    _drawNeonDart(ctx, s, color, bank = 0) {
        const secondary = this.secondaryColor || '#aa66ff';
        ctx.shadowBlur = 18;
        ctx.shadowColor = color;

        // Outer glow fill
        const hull = ctx.createLinearGradient(0, -s, 0, s);
        hull.addColorStop(0, '#ffffff');
        hull.addColorStop(0.2, color);
        hull.addColorStop(0.7, color);
        hull.addColorStop(1, shade(color, -50));
        ctx.fillStyle = hull;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.15);
        ctx.lineTo(s * 0.78, s * 0.62);
        ctx.lineTo(0, s * 0.28);
        ctx.lineTo(-s * 0.78, s * 0.62);
        ctx.closePath();
        ctx.fill();

        // Secondary wing tips (faction accent)
        ctx.fillStyle = secondary;
        ctx.globalAlpha = 0.75;
        ctx.shadowColor = secondary;
        ctx.beginPath();
        ctx.moveTo(s * 0.25, s * 0.05);
        ctx.lineTo(s * 0.85, s * 0.7);
        ctx.lineTo(s * 0.15, s * 0.35);
        ctx.closePath();
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(-s * 0.25, s * 0.05);
        ctx.lineTo(-s * 0.85, s * 0.7);
        ctx.lineTo(-s * 0.15, s * 0.35);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1;

        // Bright core
        ctx.shadowBlur = 10;
        ctx.shadowColor = '#fff';
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(0, -s * 0.55);
        ctx.lineTo(s * 0.16, s * 0.08);
        ctx.lineTo(0, s * 0.18);
        ctx.lineTo(-s * 0.16, s * 0.08);
        ctx.closePath();
        ctx.fill();

        // Outline
        ctx.shadowBlur = 0;
        ctx.strokeStyle = '#ffffffcc';
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.15);
        ctx.lineTo(s * 0.78, s * 0.62);
        ctx.lineTo(0, s * 0.28);
        ctx.lineTo(-s * 0.78, s * 0.62);
        ctx.closePath();
        ctx.stroke();

        this._bankHighlight(ctx, s, bank);
    }

    _bankHighlight(ctx, s, bank) {
        if (Math.abs(bank) <= 0.1) return;
        ctx.globalAlpha = Math.min(0.35, Math.abs(bank) * 0.4);
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        if (bank > 0) {
            ctx.moveTo(0, -s);
            ctx.lineTo(s * 0.5, s * 0.3);
            ctx.lineTo(0, 0);
        } else {
            ctx.moveTo(0, -s);
            ctx.lineTo(-s * 0.5, s * 0.3);
            ctx.lineTo(0, 0);
        }
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1;
    }

    _drawWedgeShip(ctx, s, color, bank) {
        const sec = this.secondaryColor || color;
        ctx.shadowBlur = 16;
        ctx.shadowColor = color;
        const hull = ctx.createLinearGradient(0, -s, 0, s);
        hull.addColorStop(0, '#fff');
        hull.addColorStop(0.35, color);
        hull.addColorStop(1, shade(color, -45));
        ctx.fillStyle = hull;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.05);
        ctx.lineTo(s * 1.05, s * 0.75);
        ctx.lineTo(0, s * 0.15);
        ctx.lineTo(-s * 1.05, s * 0.75);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = sec;
        ctx.globalAlpha = 0.7;
        ctx.beginPath();
        ctx.moveTo(0, -s * 0.4);
        ctx.lineTo(s * 0.55, s * 0.4);
        ctx.lineTo(0, s * 0.1);
        ctx.lineTo(-s * 0.55, s * 0.4);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.moveTo(0, -s * 0.5);
        ctx.lineTo(s * 0.12, s * 0.05);
        ctx.lineTo(-s * 0.12, s * 0.05);
        ctx.closePath();
        ctx.fill();
        this._bankHighlight(ctx, s, bank);
    }

    _drawDiamondShip(ctx, s, color, bank) {
        ctx.shadowBlur = 18;
        ctx.shadowColor = color;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.25);
        ctx.lineTo(s * 0.7, 0);
        ctx.lineTo(0, s * 1.05);
        ctx.lineTo(-s * 0.7, 0);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = '#ffffffcc';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.fillStyle = (this.secondaryColor || '#fff') + '99';
        ctx.beginPath();
        ctx.moveTo(0, -s * 0.7);
        ctx.lineTo(s * 0.3, 0);
        ctx.lineTo(0, s * 0.55);
        ctx.lineTo(-s * 0.3, 0);
        ctx.closePath();
        ctx.fill();
        this._bankHighlight(ctx, s, bank);
    }

    _drawBladeHull(ctx, s, color, bank) {
        const sec = this.secondaryColor || '#ffcc44';
        ctx.shadowBlur = 16;
        ctx.shadowColor = color;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.35);
        ctx.lineTo(s * 0.4, s * 0.15);
        ctx.lineTo(s * 0.2, s * 0.85);
        ctx.lineTo(0, s * 0.45);
        ctx.lineTo(-s * 0.2, s * 0.85);
        ctx.lineTo(-s * 0.4, s * 0.15);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = sec;
        ctx.globalAlpha = 0.8;
        ctx.beginPath();
        ctx.moveTo(s * 0.25, 0);
        ctx.lineTo(s * 1.05, s * 0.55);
        ctx.lineTo(s * 0.2, s * 0.35);
        ctx.closePath();
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(-s * 0.25, 0);
        ctx.lineTo(-s * 1.05, s * 0.55);
        ctx.lineTo(-s * 0.2, s * 0.35);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(0, -s * 0.15, s * 0.14, 0, Math.PI * 2);
        ctx.fill();
        this._bankHighlight(ctx, s, bank);
    }

    _drawRingHull(ctx, s, color, bank) {
        ctx.shadowBlur = 16;
        ctx.shadowColor = color;
        ctx.strokeStyle = color;
        ctx.lineWidth = 3.5;
        ctx.beginPath();
        ctx.ellipse(0, 0, s * 1.05, s * 0.5, 0, 0, Math.PI * 2);
        ctx.stroke();
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, s * 0.7);
        g.addColorStop(0, '#fff');
        g.addColorStop(0.4, color);
        g.addColorStop(1, shade(color, -40));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(0, 0, s * 0.6, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = this.secondaryColor || '#fff';
        ctx.beginPath();
        ctx.arc(0, -s * 0.1, s * 0.16, 0, Math.PI * 2);
        ctx.fill();
        this._bankHighlight(ctx, s, bank);
    }

    _drawSpikerHull(ctx, s, color, bank) {
        ctx.shadowBlur = 16;
        ctx.shadowColor = color;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(0, 0, s * 0.55, 0, Math.PI * 2);
        ctx.fill();
        const sec = this.secondaryColor || color;
        ctx.fillStyle = sec;
        for (let i = 0; i < 5; i++) {
            const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
            ctx.beginPath();
            ctx.moveTo(Math.cos(a - 0.3) * s * 0.4, Math.sin(a - 0.3) * s * 0.4);
            ctx.lineTo(Math.cos(a) * s * 1.2, Math.sin(a) * s * 1.2);
            ctx.lineTo(Math.cos(a + 0.3) * s * 0.4, Math.sin(a + 0.3) * s * 0.4);
            ctx.closePath();
            ctx.fill();
        }
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(0, 0, s * 0.18, 0, Math.PI * 2);
        ctx.fill();
        this._bankHighlight(ctx, s, bank);
    }

    _drawFinHull(ctx, s, color, bank) {
        ctx.shadowBlur = 16;
        ctx.shadowColor = color;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.2);
        ctx.quadraticCurveTo(s * 0.95, 0, 0, s * 0.85);
        ctx.quadraticCurveTo(-s * 0.95, 0, 0, -s * 1.2);
        ctx.fill();
        const sec = this.secondaryColor || '#88eeff';
        ctx.fillStyle = sec;
        ctx.globalAlpha = 0.7;
        ctx.beginPath();
        ctx.moveTo(s * 0.25, s * 0.05);
        ctx.lineTo(s * 1.1, s * 0.5);
        ctx.lineTo(s * 0.2, s * 0.4);
        ctx.closePath();
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(-s * 0.25, s * 0.05);
        ctx.lineTo(-s * 1.1, s * 0.5);
        ctx.lineTo(-s * 0.2, s * 0.4);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.ellipse(0, -s * 0.15, s * 0.14, s * 0.22, 0, 0, Math.PI * 2);
        ctx.fill();
        this._bankHighlight(ctx, s, bank);
    }

    _drawBoltHull(ctx, s, color, bank) {
        ctx.shadowBlur = 18;
        ctx.shadowColor = color;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.2);
        ctx.lineTo(s * 0.35, -s * 0.1);
        ctx.lineTo(s * 0.7, s * 0.05);
        ctx.lineTo(s * 0.2, s * 0.15);
        ctx.lineTo(s * 0.45, s * 0.9);
        ctx.lineTo(0, s * 0.4);
        ctx.lineTo(-s * 0.45, s * 0.9);
        ctx.lineTo(-s * 0.2, s * 0.15);
        ctx.lineTo(-s * 0.7, s * 0.05);
        ctx.lineTo(-s * 0.35, -s * 0.1);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = this.secondaryColor || '#ff88ff';
        ctx.globalAlpha = 0.75;
        ctx.beginPath();
        ctx.moveTo(0, -s * 0.6);
        ctx.lineTo(s * 0.15, s * 0.05);
        ctx.lineTo(0, s * 0.25);
        ctx.lineTo(-s * 0.15, s * 0.05);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1;
        this._bankHighlight(ctx, s, bank);
    }

    _drawPrismHull(ctx, s, color, bank) {
        const sec = this.secondaryColor || '#ff66cc';
        ctx.shadowBlur = 18;
        ctx.shadowColor = color;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.15);
        ctx.lineTo(s * 0.65, s * 0.15);
        ctx.lineTo(s * 0.25, s * 0.85);
        ctx.lineTo(-s * 0.25, s * 0.85);
        ctx.lineTo(-s * 0.65, s * 0.15);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = sec;
        ctx.globalAlpha = 0.65;
        ctx.beginPath();
        ctx.moveTo(0, -s * 0.7);
        ctx.lineTo(s * 0.35, s * 0.1);
        ctx.lineTo(0, s * 0.5);
        ctx.lineTo(-s * 0.35, s * 0.1);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = '#ffffffaa';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.15);
        ctx.lineTo(0, s * 0.85);
        ctx.stroke();
        this._bankHighlight(ctx, s, bank);
    }

    _drawBlockHull(ctx, s, color, bank) {
        ctx.shadowBlur = 14;
        ctx.shadowColor = color;
        ctx.fillStyle = shade(color, -15);
        ctx.fillRect(-s * 0.75, -s * 0.55, s * 1.5, s * 1.15);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.strokeRect(-s * 0.75, -s * 0.55, s * 1.5, s * 1.15);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.05);
        ctx.lineTo(s * 0.55, -s * 0.4);
        ctx.lineTo(-s * 0.55, -s * 0.4);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = this.secondaryColor || '#667788';
        ctx.fillRect(-s * 0.45, -s * 0.15, s * 0.35, s * 0.4);
        ctx.fillRect(s * 0.1, -s * 0.15, s * 0.35, s * 0.4);
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(0, -s * 0.1, s * 0.14, 0, Math.PI * 2);
        ctx.fill();
        this._bankHighlight(ctx, s, bank);
    }

    _drawLeafHull(ctx, s, color, bank) {
        ctx.shadowBlur = 16;
        ctx.shadowColor = color;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.15);
        ctx.bezierCurveTo(s * 0.95, -s * 0.2, s * 0.7, s * 0.75, 0, s * 0.95);
        ctx.bezierCurveTo(-s * 0.7, s * 0.75, -s * 0.95, -s * 0.2, 0, -s * 1.15);
        ctx.fill();
        ctx.strokeStyle = this.secondaryColor || '#aaff88';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(0, -s * 0.85);
        ctx.lineTo(0, s * 0.65);
        ctx.stroke();
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(0, -s * 0.1, s * 0.16, 0, Math.PI * 2);
        ctx.fill();
        this._bankHighlight(ctx, s, bank);
    }

    _drawMechHull(ctx, s, color, bank) {
        ctx.shadowBlur = 14;
        ctx.shadowColor = color;
        ctx.fillStyle = shade(color, -20);
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
            const a = (i / 6) * Math.PI * 2 - Math.PI / 6;
            const px = Math.cos(a) * s * 0.95;
            const py = Math.sin(a) * s * 0.95;
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.5;
        ctx.stroke();
        ctx.fillStyle = color;
        ctx.fillRect(-s * 1.05, -s * 0.12, s * 0.35, s * 0.24);
        ctx.fillRect(s * 0.7, -s * 0.12, s * 0.35, s * 0.24);
        ctx.fillStyle = this.secondaryColor || '#ccddee';
        ctx.beginPath();
        ctx.arc(0, 0, s * 0.28, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#0a1020';
        ctx.beginPath();
        ctx.arc(0, 0, s * 0.12, 0, Math.PI * 2);
        ctx.fill();
        this._bankHighlight(ctx, s, bank);
    }

    _drawFighterShip(ctx, s, color) {
        this._drawNeonDart(ctx, s, color, 0);
    }

    _drawDartShip(ctx, s, color) {
        const hull = ctx.createLinearGradient(0, -s, 0, s);
        hull.addColorStop(0, '#fff');
        hull.addColorStop(0.4, color);
        hull.addColorStop(1, shade(color, -50));
        ctx.fillStyle = hull;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.05);
        ctx.lineTo(s * 0.62, s * 0.55);
        ctx.lineTo(0, s * 0.2);
        ctx.lineTo(-s * 0.62, s * 0.55);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = '#dffcff';
        ctx.beginPath();
        ctx.moveTo(0, -s * 0.55);
        ctx.lineTo(s * 0.14, s * 0.05);
        ctx.lineTo(-s * 0.14, s * 0.05);
        ctx.closePath();
        ctx.fill();
    }

    _drawOrbShip(ctx, s, color) {
        const g = ctx.createRadialGradient(-s * 0.25, -s * 0.25, 2, 0, 0, s);
        g.addColorStop(0, '#ffffff');
        g.addColorStop(0.35, color);
        g.addColorStop(1, shade(color, -45));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(0, 0, s * 0.85, 0, Math.PI * 2);
        ctx.fill();
        // Orbit ring
        ctx.strokeStyle = '#ffffff88';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.ellipse(0, 0, s * 1.05, s * 0.45, -0.4, 0, Math.PI * 2);
        ctx.stroke();
        // Core
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(-s * 0.12, -s * 0.15, s * 0.18, 0, Math.PI * 2);
        ctx.fill();
    }

    _drawStarShip(ctx, s, color) {
        ctx.fillStyle = color;
        ctx.shadowBlur = 18;
        ctx.shadowColor = color;
        this.drawStar(ctx, 0, 0, 5, s, s * 0.42);
        ctx.fillStyle = '#fff';
        ctx.shadowBlur = 0;
        ctx.beginPath();
        ctx.arc(0, 0, s * 0.22, 0, Math.PI * 2);
        ctx.fill();
    }

    drawStar(ctx, cx, cy, spikes, outerRadius, innerRadius) {
        let rot = Math.PI / 2 * 3;
        let x = cx;
        let y = cy;
        const step = Math.PI / spikes;

        ctx.beginPath();
        ctx.moveTo(cx, cy - outerRadius);
        for (let i = 0; i < spikes; i++) {
            x = cx + Math.cos(rot) * outerRadius;
            y = cy + Math.sin(rot) * outerRadius;
            ctx.lineTo(x, y);
            rot += step;

            x = cx + Math.cos(rot) * innerRadius;
            y = cy + Math.sin(rot) * innerRadius;
            ctx.lineTo(x, y);
            rot += step;
        }
        ctx.lineTo(cx, cy - outerRadius);
        ctx.closePath();
        ctx.fill();
    }

    heal(amount) {
        this.hp = Math.min(this.maxHp, this.hp + amount);
    }

    addXP(amount) {
        this.xp += amount;
        return this.xp >= this.xpNeeded;
    }

    levelUp() {
        this.level++;
        this.xp = 0;
        this.xpNeeded = Math.floor(this.xpNeeded * 2.0);
    }

    applyPowerUp(powerUp) {
        const endTime = Date.now() + powerUp.type.duration;

        this.activePowerUps.push({
            type: powerUp.type.id,
            name: powerUp.type.name,
            endTime: endTime
        });

        switch (powerUp.type.id) {
            case 'speed':
                this.speed = this.baseSpeed * 2;
                break;
            case 'invincible':
                this.invincible = true;
                break;
            case 'damage':
                this.damage = this.baseDamage * 2;
                break;
            case 'multishot':
                break;
            case 'slowtime':
                break;
            case 'magnet':
                this.magnetRange = this.baseMagnetRange + 150;
                break;
        }
    }

    updatePowerUps() {
        const now = Date.now();
        const expired = [];

        this.activePowerUps.forEach((powerUp, idx) => {
            if (now >= powerUp.endTime) {
                expired.push(idx);
                this.removePowerUp(powerUp.type);
            }
        });

        for (let i = expired.length - 1; i >= 0; i--) {
            this.activePowerUps.splice(expired[i], 1);
        }
    }

    removePowerUp(type) {
        switch (type) {
            case 'speed':
                this.speed = this.baseSpeed;
                break;
            case 'invincible':
                this.invincible = false;
                break;
            case 'damage':
                this.damage = this.baseDamage;
                break;
            case 'magnet':
                this.magnetRange = this.baseMagnetRange;
                break;
        }
    }

    hasPowerUp(type) {
        return this.activePowerUps.some(p => p.type === type);
    }

    takeDamage(damage) {
        if (this.invincible) return false;
        const reduced = Math.max(0.25, 1 - (this.armor || 0) * 0.02);
        this.hp -= damage * reduced;
        this.hitFlash = 10;
        // Chance to wear equipment when hit hard
        if (damage * reduced >= 4 && Math.random() < 0.22) {
            this._pendingWear = 8 + Math.floor(Math.random() * 12);
        }
        return this.hp <= 0;
    }
}

function shade(hex, amount) {
    const c = hex.replace('#', '');
    if (c.length !== 6) return hex;
    const num = parseInt(c, 16);
    let r = (num >> 16) + amount;
    let g = ((num >> 8) & 0xff) + amount;
    let b = (num & 0xff) + amount;
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
